/**
 * @fileoverview Top 20 huurders met de meeste helpdesktickets
 *
 * Telt tickets per **melder** (het contact op het ticket). "Huurder" is niet
 * hard af te leiden uit de data — huurders op een contract hangen aan `resource`,
 * niet aan `contact`, en lang niet elke huurder heeft het contactlabel Huurder.
 * De backend filtert daarom standaard de duidelijke niet-huurders weg (interne
 * Yally-adressen en zakelijke labels zoals eigenaar of leverancier); met de
 * schakelaar "Alleen label Huurder" krijg je de strikte, kleinere lijst.
 *
 * Een tabel en geen grafiek: namen zijn lang, en per rij hoort meer context
 * (gebouw, openstaande tickets) dan een balk kan dragen.
 *
 * @module Helpdesk/Statistics/TopTenantsTable
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Badge } from "components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Users, User, Buildings } from "@phosphor-icons/react";
import { useTopTenants } from "../hooks/useStatisticsData";
import { TopTenantsFilters } from "../types/statistics.types";

const TOP_COUNT = 20;

export function TopTenantsTable() {
  const [filters, setFilters] = useState<TopTenantsFilters>({
    year: "all",
    limit: TOP_COUNT,
    onlyTaggedTenants: false,
  });

  const { data, loading } = useTopTenants(filters);
  const navigate = useNavigate();

  /**
   * Naar de ticketlijst, gefilterd op de naam van de melder. De lijst heeft geen
   * melder-filter, maar haar zoekveld doorzoekt wél de melder-naam.
   */
  const handleTenantClick = (name: string) => {
    navigate(`/helpdesk/list?search=${encodeURIComponent(name)}`);
  };

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <Skeleton className="h-5 w-56" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const tenants = data?.tenants ?? [];

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" weight="duotone" />
          <CardTitle className="text-lg">
            Top {TOP_COUNT} huurders met de meeste tickets
          </CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Geteld op de melder van het ticket
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-muted-foreground">
            {data?.totalTickets ?? 0} tickets van deze {tenants.length} melders
            {filters.year !== "all" ? ` in ${filters.year}` : " (alle jaren)"}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border bg-muted/30 p-0.5">
              <button
                onClick={() =>
                  setFilters((prev) => ({ ...prev, onlyTaggedTenants: false }))
                }
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  !filters.onlyTaggedTenants
                    ? "bg-white shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Alle melders, zonder eigenaars, leveranciers en interne adressen"
              >
                Alle melders
              </button>
              <button
                onClick={() =>
                  setFilters((prev) => ({ ...prev, onlyTaggedTenants: true }))
                }
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  filters.onlyTaggedTenants
                    ? "bg-white shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Alleen contacten met het label Huurder of Ex-huurder"
              >
                Alleen label Huurder
              </button>
            </div>

            <Select
              value={filters.year}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, year: value }))
              }
            >
              <SelectTrigger className="w-[110px]">
                <SelectValue placeholder="Jaar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle jaren</SelectItem>
                {data?.availableYears?.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-lg border">
          {tenants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <User className="mb-2 h-10 w-10 opacity-50" />
              <p>Geen melders gevonden</p>
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 border-b bg-muted/50">
                  <tr>
                    <th className="w-12 px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      #
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      Melder
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      Gebouw
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">
                      Open
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">
                      Tickets
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tenants.map((tenant, index) => (
                    <tr
                      key={tenant.contactId}
                      onClick={() => handleTenantClick(tenant.name)}
                      className="cursor-pointer transition-colors hover:bg-muted/30"
                    >
                      <td className="px-4 py-2.5 text-sm font-medium text-muted-foreground">
                        {index + 1}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <User
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            weight="duotone"
                          />
                          <span className="text-sm font-medium">
                            {tenant.name}
                          </span>
                          {/* Het label maakt zichtbaar of dit contact ook echt
                              als huurder gekend staat. */}
                          {tenant.isTaggedTenant ? (
                            <Badge variant="secondary" className="text-[10px]">
                              Huurder
                            </Badge>
                          ) : tenant.tags.length > 0 ? (
                            <Badge variant="outline" className="text-[10px]">
                              {tenant.tags[0]}
                            </Badge>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-muted-foreground">
                        {tenant.buildingIds.length === 0 ? (
                          "-"
                        ) : (
                          <span
                            className="flex items-center gap-1.5"
                            title={tenant.buildingIds.join(", ")}
                          >
                            <Buildings className="h-4 w-4 shrink-0" />
                            {tenant.buildingIds[0]}
                            {tenant.buildingIds.length > 1 && (
                              <span className="opacity-70">
                                +{tenant.buildingIds.length - 1}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm text-muted-foreground">
                        {tenant.openTicketCount}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Badge
                          variant={
                            tenant.ticketCount >= 20 ? "destructive" : "secondary"
                          }
                          className="font-mono"
                        >
                          {tenant.ticketCount}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Klik op een rij om de tickets van die melder te zien
        </p>
      </CardContent>
    </Card>
  );
}
